import { FC, useState } from 'react'
import classNames from 'classnames'
import { ReactComponent as GoogleAdsLogo } from 'src/assets/svg/google-ads.svg'
import { ReactComponent as MetaLogo } from 'src/assets/svg/meta-ads.svg'
import {
  useAdPerfCampaigns,
  useAdPerfOverview,
} from 'src/reactQuery/useAdPerformance'
import { AdPerfRange } from 'src/services/adPerformanceApi'
import { ReactComponent as TikTokLogo } from './logos/tiktok-color.svg'
import { ReactComponent as LinkedInLogo } from './logos/linkedin-color.svg'
import s from './dashboard.module.scss'
import { BulbIcon, ChevronDown } from './icons'
import { actionResults, compact, money, objectiveLabel } from '../format'

type Metric = 'clicks' | 'impressions' | 'results' | 'spend'
const METRICS: { key: Metric; label: string }[] = [
  { key: 'spend', label: 'Spend' },
  { key: 'impressions', label: 'Impressions' },
  { key: 'clicks', label: 'Clicks' },
  { key: 'results', label: 'Results' },
]

interface Totals {
  clicks: number
  impressions: number
  results: number
  spend: number
}

// Cost column follows the chosen metric, as in the design (Impressions → CPM).
const COST: Record<
  Metric,
  { label: string; of: (t: Totals) => number | null }
> = {
  clicks: { label: 'CPC', of: (t) => (t.clicks ? t.spend / t.clicks : null) },
  impressions: {
    label: 'CPM',
    of: (t) => (t.impressions ? (t.spend / t.impressions) * 1000 : null),
  },
  results: {
    label: 'Cost / result',
    of: (t) => (t.results ? t.spend / t.results : null),
  },
  spend: {
    label: 'CPM',
    of: (t) => (t.impressions ? (t.spend / t.impressions) * 1000 : null),
  },
}

// Only Meta is connected today; the others show as "Not connected".
const OTHERS = [
  { Logo: GoogleAdsLogo, label: 'Google' },
  { Logo: LinkedInLogo, label: 'LinkedIn' },
  { Logo: TikTokLogo, label: 'TikTok' },
]

interface Props {
  currency: string
  range: AdPerfRange
}

export const PlatformComparison: FC<Props> = ({ currency, range }) => {
  const [metric, setMetric] = useState<Metric>('impressions')
  const [open, setOpen] = useState(false)
  const { data: overview } = useAdPerfOverview(range)
  const { data: campaigns = [] } = useAdPerfCampaigns(range)

  const t = overview?.totals
  const meta: Totals = {
    clicks: t?.clicks ?? 0,
    impressions: t?.impressions ?? 0,
    results: campaigns.reduce((a, c) => a + actionResults(c), 0),
    spend: t?.spend ?? 0,
  }

  // "Tap a row to see objectives": Meta split by campaign objective.
  const byObjective = Object.values(
    campaigns.reduce<Record<string, Totals & { name: string }>>((acc, c) => {
      const k = c.objective ?? 'unknown'
      const o = (acc[k] ??= {
        clicks: 0,
        impressions: 0,
        name: objectiveLabel(c.objective),
        results: 0,
        spend: 0,
      })
      o.clicks += c.clicks
      o.impressions += c.impressions
      o.results += actionResults(c)
      o.spend += c.spend
      return acc
    }, {})
  )
    .filter((o) => o[metric] > 0)
    .sort((a, b) => b[metric] - a[metric])

  const fmt = (v: number) =>
    metric === 'spend' ? money(v, currency) : compact(v)
  const label = METRICS.find((m) => m.key === metric)!.label
  const cost = COST[metric]

  return (
    <div className={s.card}>
      <h3 className={s.cardTitle}>Platform Comparison</h3>
      <div className={s.chips} style={{ marginTop: 16 }}>
        {METRICS.map((m) => (
          <button
            className={classNames(s.chip, { [s.chipActive]: metric === m.key })}
            key={m.key}
            onClick={() => setMetric(m.key)}
            type="button"
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className={s.headline}>
        Meta delivers 100% of {label.toLowerCase()}
      </div>
      <div className={s.subtle}>
        {cost.label} on Meta is {money(cost.of(meta), currency, 2)}. Connect
        Google, LinkedIn or TikTok to compare platforms.
      </div>

      <div className={s.tableWrap} style={{ margin: '16px 0 0' }}>
        <table className={classNames(s.table, s.platformTable)}>
          <thead>
            <tr>
              <th>Platform</th>
              <th>{label}</th>
              {metric !== 'clicks' && <th>Clicks</th>}
              {metric !== 'results' && <th>Results</th>}
              <th>{cost.label}</th>
            </tr>
          </thead>
          <tbody>
            <tr className={s.platformRow} onClick={() => setOpen((v) => !v)}>
              <td>
                <span className={s.platformName}>
                  <ChevronDown
                    className={s.groupChevron}
                    style={open ? undefined : { transform: 'rotate(-90deg)' }}
                  />
                  <span className={s.platformDot}>
                    <MetaLogo />
                  </span>
                  Meta
                </span>
              </td>
              <td>
                <div className={s.platformShare}>
                  <strong>{fmt(meta[metric])}</strong>{' '}
                  <span className={s.small}>/ 100%</span>
                </div>
                <div className={s.progress}>
                  <span style={{ width: '100%' }} />
                </div>
              </td>
              {metric !== 'clicks' && <td>{compact(meta.clicks)}</td>}
              {metric !== 'results' && <td>{compact(meta.results)}</td>}
              <td>{money(cost.of(meta), currency, 2)}</td>
            </tr>
            {open &&
              byObjective.map((o) => (
                <tr className={s.platformSubRow} key={o.name}>
                  <td>{o.name}</td>
                  <td>
                    {fmt(o[metric])}{' '}
                    <span className={s.small}>
                      /{' '}
                      {meta[metric]
                        ? Math.round((o[metric] / meta[metric]) * 100)
                        : 0}
                      %
                    </span>
                  </td>
                  {metric !== 'clicks' && <td>{compact(o.clicks)}</td>}
                  {metric !== 'results' && <td>{compact(o.results)}</td>}
                  <td>{money(cost.of(o), currency, 2)}</td>
                </tr>
              ))}
            {OTHERS.map(({ Logo, label: name }) => (
              <tr className={s.platformOff} key={name}>
                <td>
                  <span className={s.platformName}>
                    <span className={s.groupChevron} />
                    <span className={s.platformDot}>
                      <Logo />
                    </span>
                    {name}
                  </span>
                </td>
                <td
                  colSpan={metric === 'clicks' || metric === 'results' ? 3 : 4}
                >
                  <span className={s.small}>Not connected</span>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>
                <strong>Total</strong>
              </td>
              <td>
                <strong>{fmt(meta[metric])}</strong>
              </td>
              {metric !== 'clicks' && (
                <td>
                  <strong>{compact(meta.clicks)}</strong>
                </td>
              )}
              {metric !== 'results' && (
                <td>
                  <strong>{compact(meta.results)}</strong>
                </td>
              )}
              <td>
                <strong>{money(cost.of(meta), currency, 2)}</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className={s.syncNote}>Tap a row to see objectives</p>

      {byObjective.length > 1 && (
        <div className={s.tip}>
          <BulbIcon />
          <span>
            <strong>
              {byObjective[0].name} campaigns bring{' '}
              {meta[metric]
                ? Math.round((byObjective[0][metric] / meta[metric]) * 100)
                : 0}
              % of {label.toLowerCase()}
            </strong>{' '}
            at {money(cost.of(byObjective[0]), currency, 2)} {cost.label}.
          </span>
        </div>
      )}
    </div>
  )
}
